import React, { useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import DrawerDimensions from "./GridfinityCalculator/DrawerDimensions";
import PrinterSettings from "./GridfinityCalculator/PrinterSettings";
import BinOptions from "./GridfinityCalculator/BinOptions";
import DrawerOptions from "./GridfinityCalculator/DrawerOptions";
import GridfinityResults from "./GridfinityResults";
import GridfinityVisualPreview from "./GridfinityVisualPreview";
import AdSlot from "./AdSlot";
import { useSettings } from "@/contexts/SettingsContext";
import { useGridfinityCalculation } from "@/hooks/useGridfinityCalculation";
import { useLegacyMigration } from "@/hooks/useLegacyMigration";
import { saveUserSettings, loadUserSettings } from "@/lib/utils";

const GridfinityCalculator: React.FC = () => {
  // Migrate legacy data first
  useLegacyMigration();
  
  // Use settings from context for clean state management
  const settings = useSettings();
  
  // Calculate results based on current settings
  const { result, layout, printerSize } = useGridfinityCalculation({
    drawerSize: settings.drawerSize,
    selectedPrinter: settings.selectedPrinter,
    customPrinterSize: settings.customPrinterSize,
    printProfile: settings.printProfile,
    useHalfSize: false,
    preferHalfSize: false,
    preferUniformBaseplates: settings.preferUniformBaseplates,
    numDrawers: settings.numDrawers,
  });
  
  // Maintain backward compatibility with tests by syncing with old format
  useEffect(() => {
    // Load legacy settings on mount for test compatibility
    const legacySettings = loadUserSettings();
    if (legacySettings && !localStorage.getItem('gridfinity_migration_v1')) {
      // Apply legacy settings if they exist and haven't been migrated
      if (legacySettings.drawerSize) settings.setDrawerSize(legacySettings.drawerSize);
      if (legacySettings.selectedPrinter) settings.setSelectedPrinter(legacySettings.selectedPrinter);
      if (legacySettings.printProfile) settings.setPrintProfile(legacySettings.printProfile);
      if (legacySettings.useHalfSize !== undefined) settings.setUseHalfSize(legacySettings.useHalfSize);
      if (legacySettings.preferHalfSize !== undefined) settings.setPreferHalfSize(legacySettings.preferHalfSize);
      if (legacySettings.numDrawers !== undefined) settings.setNumDrawers(legacySettings.numDrawers);
      if (legacySettings.useMm !== undefined) settings.setUseMm(legacySettings.useMm);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Save in legacy format for test compatibility
  useEffect(() => {
    saveUserSettings({
      drawerSize: settings.drawerSize,
      selectedPrinter: settings.selectedPrinter,
      printProfile: settings.printProfile,
      useHalfSize: false,
      preferHalfSize: false,
      numDrawers: settings.numDrawers,
      useMm: settings.useMm,
    });
  }, [
    settings.drawerSize,
    settings.selectedPrinter,
    settings.printProfile,
    settings.useHalfSize,
    settings.preferHalfSize,
    settings.numDrawers,
    settings.useMm,
  ]);

  return (
    <div className="space-y-6">
      <div
        className="grid gap-6 max-w-full"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gridAutoRows: "1fr",
        }}
      >
        <Card>
          <CardContent>
            <DrawerDimensions
              drawerSize={settings.drawerSize}
              setDrawerSize={settings.setDrawerSize}
              useMm={settings.useMm}
              setUseMm={settings.setUseMm}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <PrinterSettings
              selectedPrinter={settings.selectedPrinter}
              setSelectedPrinter={settings.setSelectedPrinter}
              printerSize={printerSize}
              setPrinterSize={settings.setCustomPrinterSize}
              useMm={settings.useMm}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <BinOptions
              preferUniformBaseplates={settings.preferUniformBaseplates}
              setPreferUniformBaseplates={settings.setPreferUniformBaseplates}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <DrawerOptions
              numDrawers={settings.numDrawers}
              setNumDrawers={settings.setNumDrawers}
              printProfile={settings.printProfile}
              setPrintProfile={settings.setPrintProfile}
            />
          </CardContent>
        </Card>
      </div>

      {result && (
        <>
          <GridfinityResults
            result={result}
            useMm={settings.useMm}
            useHalfSize={false}
            preferHalfSize={false}
            printProfile={settings.printProfile}
          />
          {layout && layout.length > 0 && (
            <>
              <GridfinityVisualPreview
                layout={layout}
                drawerSize={settings.drawerSize}
                useMm={settings.useMm}
                printProfile={settings.printProfile}
              />
              {import.meta.env.VITE_ADSENSE_SLOT && <AdSlot />}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default GridfinityCalculator;
